<template>
  <div class="card border mb-3">
    <div
      class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
    >
      <h5 class="mb-0">{{ $t("forwards.deals.plan.title") }}</h5>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <ActionButtonWithSpinner
          v-if="deal.capabilities.can_manage"
          :busy="busyAction === 'offering_sheet'"
          :disabled="busyAction !== null"
          variant="btn-ghost"
          @click="emit('generate-offering-sheet')"
        >
          {{ $t("forwards.deals.plan.generate_offering_sheet") }}
        </ActionButtonWithSpinner>
        <button
          v-if="
            deal.capabilities.can_manage_commitments ||
            deal.capabilities.can_correct_shipped
          "
          type="button"
          class="btn btn-sm btn-outline-primary"
          @click="emit('edit-plan')"
        >
          {{ $t("forwards.deals.plan.edit_plan") }}
        </button>
      </div>
    </div>

    <div class="card-body">
      <p
        v-if="latestOfferingSheetUrl"
        class="alert alert-subtle-info d-flex align-items-baseline gap-2 mb-3"
      >
        <FontAwesomeIcon icon="fa-regular fa-circle-info" />
        <button
          v-if="offeringSheetIsFile"
          type="button"
          class="btn btn-link p-0 align-baseline"
          :disabled="downloadingSheet"
          @click="downloadOfferingSheet"
        >
          {{ $t("forwards.deals.plan.offering_sheet_link") }}
        </button>
        <a
          v-else-if="isSafeUrl(latestOfferingSheetUrl)"
          :href="latestOfferingSheetUrl"
          target="_blank"
          rel="noopener"
          >{{ $t("forwards.deals.plan.offering_sheet_link") }}</a
        >
        <span v-else>{{ latestOfferingSheetUrl }}</span>
      </p>

      <p v-if="deal.deliveries.length === 0" class="text-body-tertiary mb-0">
        {{ $t("forwards.deals.plan.empty") }}
      </p>

      <template v-else>
        <DeliveryCardComponent
          v-for="delivery in deal.deliveries"
          :key="delivery.id"
          :delivery="delivery"
          :managesDeal="deal.capabilities.can_manage"
          :canManage="deal.capabilities.can_manage_commitments"
          :canMoveEscrow="deal.capabilities.can_manage"
          :shortfallMwh="shortfallOf(delivery.id)"
          :highlighted="delivery.id === highlightedDeliveryId"
          :scrollWhenHighlighted="scrollToHighlighted"
          :busy="busyAction !== null"
          @slice-action="emit('slice-action', $event)"
          @delivered="(deliveryId: number) => emit('delivered', deliveryId)"
          @producer-prices="
            (deliveryId: number) => emit('producer-prices', deliveryId)
          "
          @delivery-notes="
            (deliveryId: number) => emit('delivery-notes', deliveryId)
          "
          @scrolled="(deliveryId: number) => emit('scrolled', deliveryId)"
        />
      </template>

      <PlanSummaryBarComponent
        :status="deal.status"
        :capabilities="deal.capabilities"
        :openMwh="deal.flags.open_volume_mwh"
        :plannedMwh="plannedMwh"
        :committedMwh="committedMwh"
        :escrowedMwh="escrowedMwh"
        :dealVolumeMwh="deal.volume_mwh"
        :busyAction="busyAction"
        @confirm="emit('confirm')"
        @unconfirm="emit('unconfirm')"
        @cancel="emit('cancel')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import { isLocalSheetLink } from "@/forwards2/deals/dealLinks";
import DeliveryCardComponent from "@/forwards2/deals/DeliveryCardComponent.vue";
import PlanSummaryBarComponent from "@/forwards2/deals/PlanSummaryBarComponent.vue";
import type { SliceAction } from "@/forwards2/deals/sliceActions";
import type { DealAction } from "@/forwards2/deals/useDealStore";
import { forwardsApi } from "@/forwards2/forwardsApi";
import type { DealResponse } from "@/forwards2/forwardsTypes";
import {
  downloadFromBlob,
  extractFilenameFromUrl,
} from "@/utils/downloadUtils";
import { isSafeUrl } from "@/utils/urlUtils";
import { last, sumBy } from "lodash-es";
import { computed, inject, ref } from "vue";

/**
 * The deal's delivery plan — the page's primary surface: one card per delivery, its slices as rows,
 * every slice action hanging off its own row. Fixed-volume deals only: a pay-as-produced deal's plan
 * is generated, and its page shows the schedule and the monthly monitoring instead.
 */
const props = withDefaults(
  defineProps<{
    deal: DealResponse;
    /** Newest last, as the deal's links column stores them. */
    offeringSheetUrls?: string[];
    busyAction?: DealAction | null;
    /** From `?delivery=` — the overview deep-links a delivery to act on. */
    highlightedDeliveryId?: number | null;
    /** Whether the highlighted delivery's card should still scroll itself into view. */
    scrollToHighlighted?: boolean;
  }>(),
  {
    offeringSheetUrls: () => [],
    busyAction: null,
    highlightedDeliveryId: null,
    scrollToHighlighted: false,
  }
);

const emit = defineEmits<{
  "edit-plan": [];
  "generate-offering-sheet": [];
  "slice-action": [action: SliceAction];
  delivered: [deliveryId: number];
  "producer-prices": [deliveryId: number];
  "delivery-notes": [deliveryId: number];
  confirm: [];
  unconfirm: [];
  cancel: [];
  scrolled: [deliveryId: number];
}>();

const globalErrorHandler = inject("globalErrorHandler") as (
  error: unknown
) => void;

const downloadingSheet = ref(false);

const latestOfferingSheetUrl = computed(() => last(props.offeringSheetUrls));

/** A sheet built as a file rather than a Google Sheet (development, where Drive is unavailable). */
const offeringSheetIsFile = computed(() =>
  isLocalSheetLink(latestOfferingSheetUrl.value ?? "")
);

const allSlices = computed(() =>
  props.deal.deliveries.flatMap((delivery) => delivery.slices)
);

const plannedMwh = computed(() =>
  sumBy(props.deal.deliveries, (delivery) => delivery.planned_mwh)
);
const escrowedMwh = computed(() =>
  sumBy(props.deal.deliveries, (delivery) => delivery.escrowed_mwh)
);
const committedMwh = computed(() =>
  sumBy(
    allSlices.value.filter((slice) => !slice.is_open),
    (slice) => slice.volume_mwh
  )
);

const shortfallOf = (deliveryId: number) =>
  props.deal.flags.escrow_shortfall_by_delivery[String(deliveryId)] ?? 0;

const downloadOfferingSheet = async () => {
  const url = latestOfferingSheetUrl.value;
  if (!url) return;

  downloadingSheet.value = true;
  try {
    const filename = extractFilenameFromUrl(url);
    const response = await forwardsApi.downloadOfferingSheet(
      props.deal.id,
      filename
    );
    downloadFromBlob(response.data, filename);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    downloadingSheet.value = false;
  }
};
</script>
